import { Directive, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController, NavController } from '@ionic/angular';
import { MonthlyEvaluationModalComponent } from 'src/app/pages/evaluation/monthly-evaluation-modal/monthly-evaluation-modal.component';
import { OneOffEvaluationModalComponent } from 'src/app/pages/evaluation/one-off-evaluation-modal/one-off-evaluation-modal.component';
import { WeeklyEvaluationStep1 } from 'src/app/pages/evaluation/weekly-evaluation/step1/step1.page';
import { CreateJobPopupComponent } from '../../pages/jobs/create-job-popup/create-job-popup.component';
import { Require } from '../components/component.util';
import { ServicesProvider } from '../services/services.provider';
import { UtilityService } from '../services/utility.service';

// data is marked as a required field
@Require<OpenLinkButtonDirective>(['link'])
@Directive({
  selector: '[mableOpenLinkButton]',
})
export class OpenLinkButtonDirective {
  @Input() link: {
    ctaLink?: string;
    ctaType?: string;
    ctaAction?: string;
    ctaData?: any;
  };

  @Output() onActionCompletion = new EventEmitter();

  constructor(
    public navCtrl: NavController,
    protected modalCtrl: ModalController,
    private servicesProvider: ServicesProvider,
    private utilityService: UtilityService,
    private router: Router,
  ) {}

  @HostListener('click', ['$event.target'])
  onClick() {
    this.openLink();
  }

  async openLink() {
    switch (this.link.ctaType) {
      // Redirect user to mobile feature
      case 'internal':
        this.openInternalLink(this.link.ctaLink);
        this.onActionCompletion.emit();
        break;
      // Call backend API
      case 'ping': {
        const callSuccessful = await this.servicesProvider.backendGetCall(this.link.ctaLink);
        this.onActionCompletion.emit(callSuccessful);
        break;
      }
      // opens the provided url in the system browser
      case 'external':
      default:
        this.utilityService.openExternalLink(this.link.ctaLink);
        this.onActionCompletion.emit();
        break;
    }
  }

  // Section name passed by backend is being mapped to a mobile feature.
  async openInternalLink(section: string) {
    switch (section) {
      case 'jobs':
        this.navCtrl.navigateForward('/tabs/jobs');
        break;
      case 'searchWorkers':
        this.navCtrl.navigateForward('/search/triage');
        break;
      case 'postAJob':
        // TODO-SM: move css and backdrop logic to component
        this.modalCtrl
          .create({
            component: CreateJobPopupComponent,
            showBackdrop: true,
            cssClass: 'fullpage-modal',
          })
          .then((modal) => {
            // https://github.com/ionic-team/ionic-framework/issues/26872
            modal.shadowRoot.querySelector('.modal-wrapper').removeAttribute('aria-modal');

            modal.present();
          });
        break;
      case 'clientAccount':
        this.navCtrl.navigateForward('client-account');
        break;
      case 'clientAbout':
        this.navCtrl.navigateForward('client-account/about');
        break;
      case 'clientNeeds':
        this.navCtrl.navigateForward('client-account/supportneeds-details');
        break;
      case 'clientHousehold':
        this.navCtrl.navigateForward('client-account/household-details');
        break;
      case 'weeklyReliability':
        this.openEvaluationForm(WeeklyEvaluationStep1, 'weekly-evaluation-modal');
        break;
      case 'monthlyReliability':
        this.openEvaluationForm(MonthlyEvaluationModalComponent, 'monthly-evaluation-modal');
        break;
      case 'oneoffReliability':
        this.openEvaluationForm(OneOffEvaluationModalComponent, 'one-off-evaluation-modal');
        break;
      case 'verifyMobile':
        this.router.navigate(['onboarding/verify-mobile']);
        break;
      case 'yourName':
        this.router.navigate(['onboarding/your-name']);
        break;
      case 'profilePhoto':
        this.router.navigate(['onboarding/profile-photo']);
        break;
      case 'lastMinuteJobsList':
        this.router.navigate(['/tabs/jobs/carer'], { state: { id: 'flex' } });
        break;
      case 'inbox':
        this.router.navigate(['/tabs/inbox']);
        break;
      case 'supportHours':
        this.router.navigate(['/tabs/timesheets']);
        break;
      case 'sirs':
        this.router.navigate(['/educate-support-worker/sirs/step1']);
        break;
      case 'wcc':
        this.router.navigate(['/educate-support-worker/wcc/step1']);
        break;
      case 'taxCalculator':
        this.router.navigate(['/accounts/billingpayment/taxcalculator']);
        break;
      case 'workers':
        this.router.navigate(['/tabs/manage-workers']);
        break;
      default:
        break;
    }
  }

  /**
   * Open either weekly, monthly or one-off evaluation modals. On dismissing the modal, we refresh the dashboard if we need to.
   *
   * @param evaluationModalComponentClass - component class of evaluation modal
   * @param modalId - id to use on the modal HTML element. This is the id used when telling ionic to dismiss the modal
   * (necessary when the modal we need to close is not the topmost modal)
   */
  async openEvaluationForm(evaluationModalComponentClass, modalId: string) {
    const evaluationModal = await this.modalCtrl.create({
      component: evaluationModalComponentClass,
      componentProps: { assessmentId: this.link.ctaData['id'] },
      showBackdrop: true,
      cssClass: 'fullpage-modal',
      id: modalId, // id necessary to close evaluationModal when there is another modal on top of it
    });

    // https://github.com/ionic-team/ionic-framework/issues/26872
    evaluationModal.shadowRoot.querySelector('.modal-wrapper').removeAttribute('aria-modal');

    await evaluationModal.present();
  }
}
